// Section « compatible MCP » de la page d'accueil.
//
// Raison d'être : le connecteur Claude existe (/claude, /api/mcp) mais rien
// sur le site ne le disait. Cette section l'annonce là où les visiteurs
// passent, donne l'adresse à saisir et les trois gestes qui suffisent, et
// renvoie vers /claude pour la suite (connexion des régies).
//
// Elle ne fait aucun appel réseau : l'adresse est dérivée de l'origine de
// l'API, comme sur la page /claude, pour qu'un environnement de test affiche
// SON adresse et non celle de la production.
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../../i18n/LanguageContext';

const API_ORIGIN = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, '');
const MCP_URL = `${API_ORIGIN}/api/mcp`;
const CLAUDE_CODE_CMD = `claude mcp add --transport http onebotads ${MCP_URL}`;

const card = {
  background: 'linear-gradient(160deg, rgba(24,19,60,.72), rgba(11,8,32,.88))',
  border: '1px solid rgba(255,255,255,0.09)',
  borderRadius: 16,
  boxShadow: '0 16px 40px -18px rgba(0,0,0,0.6)',
};

const mono = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 13.5,
  color: '#e9e6ff',
  wordBreak: 'break-all',
};

function CopyField({ value, label, t }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { /* presse-papiers refusé : la valeur reste lisible */ }
  };
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '10px 12px' }}>
        <span style={{ ...mono, flex: 1 }}>{value}</span>
        <button type="button" onClick={copy} style={{
          border: 0, borderRadius: 8, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
          background: copied ? 'rgba(94,234,212,0.18)' : 'linear-gradient(135deg,#6D5DFB,#4F46E5)',
          color: copied ? '#5eead4' : '#fff', whiteSpace: 'nowrap',
        }}>{copied ? t('landing.mcp.copied') : t('landing.mcp.copy')}</button>
      </div>
    </div>
  );
}

const McpSection = () => {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const steps = [t('landing.mcp.step1'), t('landing.mcp.step2'), t('landing.mcp.step3')];

  return (
    <section id="mcp" ref={ref} style={{ background: 'radial-gradient(120% 80% at 50% 100%, #120E33 0%, #08061E 62%)', padding: '96px 24px', position: 'relative', overflow: 'hidden' }}>
      <div className="oba-aurora" style={{ bottom: '-10%', left: '-14%', width: 560, height: 560, background: 'radial-gradient(circle, rgba(167,139,250,.18) 0%, transparent 68%)' }} />

      <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1, opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(18px)', transition: 'opacity .6s ease, transform .6s ease' }}>

        {/* En-tête */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <span style={{ display: 'inline-block', padding: '5px 16px', borderRadius: 999, background: 'rgba(94,234,212,0.1)', border: '1px solid rgba(94,234,212,0.35)', color: '#5eead4', fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 18 }}>
            {t('landing.mcp.badge')}
          </span>
          <h2 style={{ fontSize: 'clamp(26px,4vw,42px)', fontWeight: 900, color: '#ffffff', letterSpacing: '-0.03em', marginBottom: 12, lineHeight: 1.2 }}>
            {t('landing.mcp.title1')}{' '}
            <span style={{ background: 'linear-gradient(120deg,#a78bfa,#818cf8,#38bdf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{t('landing.mcp.title2')}</span>
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.62)', maxWidth: 560, margin: '0 auto', lineHeight: 1.65 }}>
            {t('landing.mcp.sub')}
          </p>
        </div>

        {/* Deux colonnes : configuration / étapes */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, alignItems: 'stretch' }}>

          <div style={{ ...card, padding: '26px 26px 22px', display: 'flex', flexDirection: 'column', gap: 18 }}>
            <CopyField value={MCP_URL} label={t('landing.mcp.urlLabel')} t={t} />
            <CopyField value={CLAUDE_CODE_CMD} label={t('landing.mcp.codeLabel')} t={t} />
            <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
              {t('landing.mcp.note')}
            </p>
          </div>

          <div style={{ ...card, padding: '26px 26px 22px', display: 'flex', flexDirection: 'column' }}>
            <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {steps.map((s, i) => (
                <li key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <span style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#fff', background: 'linear-gradient(135deg,#6D5DFB,#4F46E5)' }}>{i + 1}</span>
                  <span style={{ fontSize: 14.5, color: 'rgba(255,255,255,0.85)', lineHeight: 1.55, paddingTop: 4 }}>{s}</span>
                </li>
              ))}
            </ol>
            <div style={{ marginTop: 'auto', paddingTop: 22, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, color: '#c7bfff', fontWeight: 600 }}>{t('landing.mcp.tools')}</span>
              <Link to="/claude" style={{
                display: 'inline-block', padding: '10px 18px', borderRadius: 8, textDecoration: 'none',
                background: 'linear-gradient(135deg,#6D5DFB,#4F46E5)', color: '#fff', fontSize: 14, fontWeight: 700,
                boxShadow: '0 6px 20px -4px rgba(109,93,251,0.6)',
              }}>{t('landing.mcp.cta')} →</Link>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default McpSection;
